import forge from 'apis/forge';
import { toast } from 'react-toastify';
import type { DesignSystemContent } from 'designSystem/content/types';
import { symbolDisplayName } from 'designSystem/content/util';
import { DsFile } from 'dragonmouse-shared/interfaces';
import { v4 as uuid } from 'uuid';
import type { DsFileOptimisticWrite } from 'redux/mode/actions';
import {
	clearDsPicks,
	removeDsTab,
	addDsBreakageNotification,
} from 'redux/mode/actions';
import type { DsBreakageNotification } from 'redux/mode/reducers';
import store from 'redux/store';
import type { DsPick } from 'dragonmouse-shared/interfaces';

// Applies optimistic writes to a DsFile list, returns patched list.
// Delete writes (content === '') remove the file.
// Upsert writes add or replace the file.
export function applyDsFileWrites(files: DsFile[], writes: DsFileOptimisticWrite[]): DsFile[] {
	const map = new Map<string, DsFile>();
	for (const f of files) {
		map.set(f.path, f);
	}
	for (const write of writes) {
		if (write.content === '') {
			map.delete(write.path);
		} else {
			map.set(write.path, { path: write.path, content: write.content });
		}
	}
	// returning a sorted result, deterministic results help the stability of the rest of the system
	return Array.from(map.values()).sort((a, b) => a.path.localeCompare(b.path));
}

const DEFAULT_MAX_AGE_MS = 60000;

// Checks which writes are resolved by comparing against authoritative files.
// Returns array of write IDs that should be resolved.
export function findResolvedDsFileWrites(
	writes: DsFileOptimisticWrite[],
	authoritativeFiles: DsFile[],
	maxAgeMs: number = DEFAULT_MAX_AGE_MS
): string[] {
	const now = Date.now();
	const authByPath = new Map(authoritativeFiles.map((f) => [f.path, f.content]));

	const newestByPath = new Map<string, DsFileOptimisticWrite>();
	for (const write of writes) {
		const existing = newestByPath.get(write.path);
		if (!existing || write.createdAt > existing.createdAt) {
			newestByPath.set(write.path, write);
		}
	}

	const resolved: string[] = [];
	for (const write of writes) {
		if (now - write.createdAt > maxAgeMs) {
			resolved.push(write.id);
			continue;
		}
		const newest = newestByPath.get(write.path);
		if (newest && newest.id !== write.id) {
			resolved.push(write.id);
			continue;
		}
		const authContent = authByPath.get(write.path);
		if (write.content === '') {
			if (authContent === undefined) {
				resolved.push(write.id);
			}
		} else {
			if (authContent === write.content) {
				resolved.push(write.id);
			}
		}
	}
	return resolved;
}

export function createDsFileWrites(
	files: Array<{ path: string; content: string }>
): DsFileOptimisticWrite[] {
	const now = Date.now();
	return files.map((f) => ({
		id: uuid(),
		path: f.path,
		content: f.content,
		createdAt: now,
	}));
}

// Removes selected DS picks (tokens/components) from design system files via updateDsFiles.
// Dispatches clearDsPicks on success. Shows toast on error.
export const deleteDsPicks = async (
	designSystemId: string,
	picks: DsPick[],
	designSystemContent: DesignSystemContent
): Promise<void> => {
	const files = designSystemContent.files;
	if (!files || files.length === 0) {
		toast.error('No design system files to modify');
		return;
	}

	const tokenCssVariables = new Set<string>();
	const componentTagNames = new Set<string>();
	const recipeClassNames = new Set<string>();

	const pathsToDelete = new Set<string>();

	const ok = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

	for (const pick of picks) {
		if (pick.kind === 'token' && ok(pick.cssVariable)) tokenCssVariables.add(pick.cssVariable);
		else if (pick.kind === 'component' && ok(pick.tagName)) componentTagNames.add(pick.tagName);
		else if (pick.kind === 'recipe' && ok(pick.className)) recipeClassNames.add(pick.className);
		else if (pick.kind === 'guideline' && ok(pick.guidelinePath))
			pathsToDelete.add(pick.guidelinePath);
		else if (pick.kind === 'gallery' && ok(pick.galleryPath)) pathsToDelete.add(pick.galleryPath);
		else if (pick.kind === 'asset' && ok(pick.assetPath)) pathsToDelete.add(pick.assetPath);
	}

	const updates: { path: string; content: string }[] = [];

	const tokenFiles = files.filter(
		(f) =>
			(f.path.startsWith('theme/') && f.path.endsWith('.css')) ||
			(f.path.startsWith('styles/') && f.path.endsWith('.css'))
	);
	for (const file of tokenFiles) {
		let content = file.content;
		for (const cssVar of tokenCssVariables) {
			const escaped = cssVar.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const regex = new RegExp(`\\s*${escaped}:\\s*[\\s\\S]*?;\\s*`, 'g');
			content = content.replace(regex, '');
		}
		content = content.replace(/\n{3,}/g, '\n\n').trim();
		if (content !== file.content) {
			updates.push({ path: file.path, content });
		}
	}

	for (const tagName of componentTagNames) {
		const name = tagName.replace(/^ds-/, '');
		const basePath = `components/${name}/${name}`;
		const paths = [`${basePath}.js`, `${basePath}.css`, `${basePath}.example.json`];
		for (const path of paths) {
			const exists = files.some((f) => f.path === path);
			if (exists) {
				updates.push({ path, content: path.endsWith('.json') ? '{}' : '' });
			}
		}
	}

	if (recipeClassNames.size > 0) {
		const recipeFilesToUpdate = files.filter(
			(f) => f.path.startsWith('styles/recipes/') && f.path.endsWith('.css')
		);
		for (const file of recipeFilesToUpdate) {
			let content = file.content;
			for (const className of recipeClassNames) {
				const escaped = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
				const regex = new RegExp(
					`(?:\\/\\*[\\s\\S]*?\\*\\/\\s*)?${escaped}\\s*\\{[^}]*\\}\\s*`,
					'g'
				);
				content = content.replace(regex, '');
			}
			content = content.replace(/\n{3,}/g, '\n\n').trim();
			if (content !== file.content) {
				updates.push({ path: file.path, content });
			}
		}
	}

	for (const path of pathsToDelete) {
		const exists = files.some((f) => f.path === path);
		if (exists) {
			updates.push({ path, content: '' });
		}
	}

	if (updates.length === 0) {
		toast.error('Nothing to delete');
		return;
	}

	// Capture undo snapshots before applying updates
	const undoFiles: { path: string; content: string }[] = [];
	for (const u of updates) {
		const original = files.find((f) => f.path === u.path);
		undoFiles.push({ path: u.path, content: original?.content ?? '' });
	}

	try {
		await forge.updateDsFiles({ designSystemId, files: updates });

		const { dsTabs } = store.getState().mode;
		for (const tab of dsTabs) {
			if (
				tab.kind === 'components' &&
				tab.contentPath &&
				componentTagNames.has(`ds-${tab.contentPath}`)
			) {
				store.dispatch(removeDsTab(tab.id));
			} else if (tab.contentPath && pathsToDelete.has(tab.contentPath)) {
				store.dispatch(removeDsTab(tab.id));
			}
		}
		store.dispatch(clearDsPicks());

		// Rising-edge detection: check if removed symbols have dependents
		const graph = designSystemContent.graph;
		if (graph) {
			const removedSymbols: string[] = [];
			for (const v of tokenCssVariables) removedSymbols.push(v);
			for (const t of componentTagNames) removedSymbols.push(t);
			for (const c of recipeClassNames) removedSymbols.push(c.replace(/^\./, ''));

			const impacts: DsBreakageNotification['impacts'] = [];
			for (const sym of removedSymbols) {
				const dependents = graph.usedBy[sym];
				if (dependents && dependents.length > 0) {
					for (const dep of dependents) {
						if (!removedSymbols.includes(dep)) {
							impacts.push({ source: dep, target: sym });
						}
					}
				}
			}

			if (impacts.length > 0) {
				const allSymbols = new Set([...removedSymbols, ...impacts.map((i) => i.source)]);
				const displayNames: Record<string, string> = {};
				for (const sym of allSymbols) {
					displayNames[sym] = symbolDisplayName(sym);
				}

				store.dispatch(
					addDsBreakageNotification({
						id: uuid(),
						designSystemId,
						removedSymbols,
						impacts,
						timestamp: Date.now(),
						undoFiles,
						displayNames,
					})
				);
			}
		}
	} catch (err) {
		console.error('Failed to delete DS picks:', err);
		toast.error('Failed to delete from design system');
	}
};
